import { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Loader2, AlertTriangle, CheckCircle, Shield, KeyRound, Clock } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { HeirShell } from '@/components/HeirShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EventCard } from '@/components/nip41/EventCard';
import { KeyChainDiagram, WindowCountdown } from '@/components/nip41/KeyChainDiagram';
import {
  NIP41_KIND_WHITELIST,
  NIP41_KIND_MIGRATION,
  NIP41_KIND_TIMESTAMP_PROOF,
  NIP41_CONTESTATION_WINDOW_MS,
  LS_FIRST_SEEN_PREFIX,
  LS_RECENT_INSPECTED,
  isValidWhitelistEvent,
  isValidMigrationEvent,
  getWhitelistSuccessor,
  getMigrationOldPubkey,
  getMigrationWhitelistRef,
  getMigrationProofRef,
  type Nip41ChainState,
} from '@/lib/nip41';
import { cn } from '@/lib/utils';

export default function InspectPage() {
  useSeoMeta({
    title: 'Inspect — Heir (NIP-41)',
    description: 'Inspect any npub for NIP-41 migration state.',
  });

  const { npub: routeNpub } = useParams<{ npub?: string }>();
  const navigate = useNavigate();
  const [inputNpub, setInputNpub] = useState(routeNpub || '');
  const [inspectedNpub, setInspectedNpub] = useState(routeNpub || '');

  useEffect(() => {
    if (routeNpub) {
      setInputNpub(routeNpub);
      setInspectedNpub(routeNpub);
    }
  }, [routeNpub]);

  const handleInspect = () => {
    if (!inputNpub.startsWith('npub1')) return;
    navigate(`/inspect/${inputNpub}`);
    setInspectedNpub(inputNpub);
    saveRecentInspect(inputNpub);
  };

  return (
    <HeirShell>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Inspect</h1>
          <p className="text-slate-400">
            Query any npub for its NIP-41 migration state and chain history.
          </p>
        </div>

        {/* Search */}
        <div className="flex space-x-3 mb-8">
          <Input
            value={inputNpub}
            onChange={(e) => setInputNpub(e.target.value)}
            placeholder="Enter npub..."
            className="flex-1 bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/50"
            onKeyDown={(e) => e.key === 'Enter' && handleInspect()}
          />
          <Button
            onClick={handleInspect}
            disabled={!inputNpub.startsWith('npub1')}
            className="bg-cyan-600 hover:bg-cyan-500 text-white"
          >
            <Search className="w-4 h-4 mr-2" />
            Inspect
          </Button>
        </div>

        {/* Results */}
        {inspectedNpub && <InspectResults npub={inspectedNpub} />}

        {/* Recent */}
        <RecentInspected />
      </div>
    </HeirShell>
  );
}

function InspectResults({ npub }: { npub: string }) {
  const { nostr } = useNostr();

  const { data: chain, isLoading, error } = useQuery({
    queryKey: ['nip41-inspect', npub],
    queryFn: async () => {
      const decoded = nip19.decode(npub);
      if (decoded.type !== 'npub') throw new Error('Invalid npub');
      const pubkey = decoded.data;

      // Query whitelist events for this pubkey
      const whitelistEvents = await nostr.query([{
        kinds: [NIP41_KIND_WHITELIST],
        authors: [pubkey],
      }]);

      // Query migration events FROM this pubkey (as old key)
      const migrationEvents = await nostr.query([{
        kinds: [NIP41_KIND_MIGRATION],
        '#p': [pubkey],
      }]);

      // Query migration events BY this pubkey (as successor)
      const successorMigrations = await nostr.query([{
        kinds: [NIP41_KIND_MIGRATION],
        authors: [pubkey],
      }]);

      // Find whitelist proof events
      const whitelistIds = whitelistEvents.map(e => e.id);
      const proofEvents = whitelistIds.length > 0 ? await nostr.query([{
        kinds: [NIP41_KIND_TIMESTAMP_PROOF],
        '#e': whitelistIds,
      }]) : [];

      // Build chain state
      const state = buildChainState(
        pubkey,
        npub,
        whitelistEvents,
        migrationEvents,
        successorMigrations,
        proofEvents
      );

      return state;
    },
    enabled: !!npub,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
        <span className="ml-3 text-slate-400">Querying relays...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-950/20 border border-red-500/30 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-3" />
        <p className="text-red-300">Failed to query relays</p>
        <p className="text-sm text-red-400/60 mt-1">{(error as Error).message}</p>
      </div>
    );
  }

  if (!chain) return null;

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Migration State</h2>
          <StateBadge state={chain.state} />
        </div>

        <KeyChainDiagram chain={chain} />

        {/* Window countdown if migrating */}
        {chain.state === 'migrating' && chain.firstSeenAt && (
          <div className="mt-6 pt-6 border-t border-slate-800">
            <WindowCountdown firstSeenAt={chain.firstSeenAt} />
          </div>
        )}
      </div>

      {/* Validation Checks */}
      <ValidationChecks chain={chain} />

      {/* Raw Events */}
      {(chain.whitelistEvent || chain.migrationEvent) && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Raw Events</h3>
          {chain.whitelistEvent && (
            <EventCard
              event={chain.whitelistEvent}
              title="Whitelist Event"
              status={chain.whitelistProof ? 'confirmed' : 'unknown'}
            />
          )}
          {chain.whitelistProof && (
            <EventCard
              event={chain.whitelistProof}
              title="Whitelist Timestamp Proof"
              status="confirmed"
            />
          )}
          {chain.migrationEvent && (
            <EventCard
              event={chain.migrationEvent}
              title="Migration Event"
              status={chain.migrationProof ? 'confirmed' : 'unknown'}
            />
          )}
        </div>
      )}
    </div>
  );
}

function StateBadge({ state }: { state: Nip41ChainState['state'] }) {
  const config = {
    unarmed: { icon: KeyRound, text: 'Unarmed', class: 'bg-slate-800 text-slate-400 border-slate-700' },
    armed: { icon: Shield, text: 'Armed', class: 'bg-cyan-950/50 text-cyan-400 border-cyan-500/30' },
    migrating: { icon: Clock, text: 'In Window', class: 'bg-amber-950/50 text-amber-400 border-amber-500/30' },
    settled: { icon: CheckCircle, text: 'Settled', class: 'bg-green-950/50 text-green-400 border-green-500/30' },
    contested: { icon: AlertTriangle, text: 'Contested', class: 'bg-red-950/50 text-red-400 border-red-500/30' },
  };

  const c = config[state];
  const Icon = c.icon;

  return (
    <span className={cn('flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium', c.class)}>
      <Icon className="w-4 h-4" />
      <span>{c.text}</span>
    </span>
  );
}

function ValidationChecks({ chain }: { chain: Nip41ChainState }) {
  const checks = [];

  if (chain.whitelistEvent) {
    checks.push({
      label: 'Whitelist signature valid',
      pass: true, // We trust the relay returned it
      detail: 'Event returned by relay',
    });
    checks.push({
      label: 'Whitelist has exactly one successor p-tag',
      pass: isValidWhitelistEvent(chain.whitelistEvent),
      detail: getWhitelistSuccessor(chain.whitelistEvent)
        ? `Successor: ${getWhitelistSuccessor(chain.whitelistEvent)?.slice(0, 16)}...`
        : 'Missing p-tag',
    });
  }

  if (chain.migrationEvent) {
    checks.push({
      label: 'Migration signature valid',
      pass: true,
      detail: 'Event returned by relay',
    });
    checks.push({
      label: 'Migration pubkey matches whitelist p-tag',
      pass: chain.migrationEvent.pubkey === getWhitelistSuccessor(chain.whitelistEvent),
      detail: `Migration by: ${chain.migrationEvent.pubkey.slice(0, 16)}...`,
    });
    checks.push({
      label: 'Window elapsed (first-seen + 60 days)',
      pass: chain.firstSeenAt
        ? Date.now() - chain.firstSeenAt > NIP41_CONTESTATION_WINDOW_MS
        : false,
      detail: chain.firstSeenAt
        ? `First seen: ${new Date(chain.firstSeenAt).toLocaleDateString()}`
        : 'Never seen locally',
    });
    checks.push({
      label: 'No competing migration with older proof',
      pass: chain.state !== 'contested',
      detail: chain.state === 'contested'
        ? 'Competing migration detected'
        : 'No competitors found',
    });
  }

  if (checks.length === 0) return null;

  return (
    <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Validation Checks</h3>
      <div className="space-y-3">
        {checks.map((check, i) => (
          <div
            key={i}
            className={cn(
              'flex items-start space-x-3 p-3 rounded-lg border',
              check.pass
                ? 'bg-green-950/20 border-green-500/20'
                : 'bg-amber-950/20 border-amber-500/20'
            )}
          >
            {check.pass ? (
              <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            )}
            <div>
              <p className={cn('text-sm font-medium', check.pass ? 'text-green-400' : 'text-amber-400')}>
                {check.label}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">{check.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildChainState(
  pubkey: string,
  npub: string,
  whitelistEvents: any[],
  migrationEvents: any[],
  successorMigrations: any[],
  proofEvents: any[]
): Nip41ChainState {
  // Determine state
  let state: Nip41ChainState['state'] = 'unarmed';
  const whitelistEvent = whitelistEvents[0];
  const whitelistProof = whitelistEvent
    ? proofEvents.find(p => p.tags.some((t: string[]) => t[0] === 'e' && t[1] === whitelistEvent.id))
    : undefined;

  const migrationEvent = migrationEvents[0] || successorMigrations[0];

  if (migrationEvent) {
    // Check first-seen time
    const firstSeenKey = LS_FIRST_SEEN_PREFIX + migrationEvent.id;
    let firstSeenAt = parseInt(localStorage.getItem(firstSeenKey) || '0');
    if (!firstSeenAt) {
      firstSeenAt = Date.now();
      localStorage.setItem(firstSeenKey, firstSeenAt.toString());
    }

    const windowElapsed = Date.now() - firstSeenAt > NIP41_CONTESTATION_WINDOW_MS;
    state = windowElapsed ? 'settled' : 'migrating';

    // Check for contested state (simplified)
    if (migrationEvents.length > 1) {
      state = 'contested';
    }

    return {
      pubkey,
      npub,
      state,
      whitelistEvent,
      whitelistProof,
      migrationEvent,
      firstSeenAt,
      contestationEndAt: firstSeenAt + NIP41_CONTESTATION_WINDOW_MS,
    };
  }

  if (whitelistEvent) {
    state = 'armed';
  }

  return {
    pubkey,
    npub,
    state,
    whitelistEvent,
    whitelistProof,
  };
}

function saveRecentInspect(npub: string) {
  try {
    const raw = localStorage.getItem(LS_RECENT_INSPECTED);
    const recent: string[] = raw ? JSON.parse(raw) : [];
    const updated = [npub, ...recent.filter(n => n !== npub)].slice(0, 5);
    localStorage.setItem(LS_RECENT_INSPECTED, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

function RecentInspected() {
  const [recent, setRecent] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_RECENT_INSPECTED);
      if (raw) setRecent(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  if (recent.length === 0) return null;

  return (
    <div className="mt-8">
      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Recently Inspected</h3>
      <div className="flex flex-wrap gap-2">
        {recent.map(npub => (
          <button
            key={npub}
            onClick={() => navigate(`/inspect/${npub}`)}
            className="px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs font-mono text-slate-400 hover:border-cyan-500/30 hover:text-cyan-400 transition-colors"
          >
            {npub.slice(0, 20)}...{npub.slice(-8)}
          </button>
        ))}
      </div>
    </div>
  );
}
